import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  BookOpen, 
  FileText, 
  Users, 
  BarChart3, 
  Shield, 
  LogOut,
  Menu,
  Brain,
  ClipboardList,
  HardDrive,
  Mic,
  Settings as SettingsIcon
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

import api from '../lib/api';

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isStudent = user.role === 'user';

  // Security lockdown for student accounts: block DevTools, right-click, copy, etc.
  useEffect(() => {
    if (!isStudent) return;

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e) => {
      // F12
      if (e.key === 'F12') { e.preventDefault(); e.stopPropagation(); return false; }
      // Ctrl+Shift+I/J/C (DevTools)
      if (e.ctrlKey && e.shiftKey && ['I','i','J','j','C','c'].includes(e.key)) { e.preventDefault(); e.stopPropagation(); return false; }
      // Ctrl+U (View Source)
      if (e.ctrlKey && (e.key === 'U' || e.key === 'u')) { e.preventDefault(); return false; }
      // Ctrl+C/V/A/S/P
      if (e.ctrlKey && ['C','c','V','v','A','a','S','s','P','p'].includes(e.key)) { e.preventDefault(); return false; }
      // PrintScreen
      if (e.key === 'PrintScreen') { e.preventDefault(); return false; }
    };

    const handleCopy = (e) => e.preventDefault();
    const handlePaste = (e) => e.preventDefault();
    const handleCut = (e) => e.preventDefault();
    const handleDrag = (e) => e.preventDefault();

    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('copy', handleCopy, true);
    document.addEventListener('paste', handlePaste, true);
    document.addEventListener('cut', handleCut, true);
    document.addEventListener('dragstart', handleDrag, true);
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('copy', handleCopy, true);
      document.removeEventListener('paste', handlePaste, true);
      document.removeEventListener('cut', handleCut, true);
      document.removeEventListener('dragstart', handleDrag, true);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, [isStudent]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout-track');
    } catch (e) {
      // ignore - best effort
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  // Faculty restricted pages
  const facultyDenied = ['/admin/public-registrations', '/admin/cleanup-attempts', '/admin/audit-logs', '/admin/users'];
  const isFaculty = user.role === 'faculty';
  const isSuperAdmin = user.role === 'super_admin';

  const adminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/question-banks', icon: BookOpen, label: 'Question Banks' },
    { to: '/admin/tests', icon: FileText, label: 'Tests' },
    { to: '/admin/users', icon: Users, label: 'Users' },
    { to: '/admin/public-registrations', icon: Users, label: 'Public Registrations' },
    { to: '/admin/batches', icon: Users, label: 'Batches' },
    { to: '/admin/rankings', icon: BarChart3, label: 'Batch Rankings' },
    { to: '/admin/test-rankings', icon: BarChart3, label: 'Test Rankings' },
    { to: '/admin/psychometric', icon: Brain, label: 'Psychometric Results' },
    { to: '/admin/proctoring', icon: Shield, label: 'AI Proctoring' },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/admin/cleanup-attempts', icon: Shield, label: 'Cleanup' },
    { to: '/admin/audit-logs', icon: Shield, label: 'Audit Logs' },
    ...(isSuperAdmin ? [{ to: '/admin/faculty-reports', icon: ClipboardList, label: 'Faculty Reports' }] : []),
    ...(isSuperAdmin ? [{ to: '/admin/google-drive', icon: HardDrive, label: 'Google Drive' }] : []),
    ...(isSuperAdmin ? [{ to: '/admin/settings', icon: SettingsIcon, label: 'Settings' }] : []),
    { to: '/admin/interview', icon: Mic, label: 'Oral Assessment' },
  ].filter(link => !isFaculty || !facultyDenied.includes(link.to));

  const userLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  ];

  const links = ['admin', 'super_admin', 'faculty'].includes(user.role) ? adminLinks : userLinks;

  const renderNavLinks = () => (
    <nav className="space-y-2">
      {links.map((link) => {
        const Icon = link.icon;
        const isActive = location.pathname === link.to || location.pathname.startsWith(link.to + '/');
        return (
          <Link
            key={link.to}
            to={link.to}
            data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
              isActive
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Icon className="w-5 h-5" />
            <span className="font-medium">{link.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 px-4 flex items-center justify-between z-50">
        <div className="flex items-center">
          <img src="/himt-logo.png" alt="HIMT Logo" className="h-8 w-auto mr-2" />
          <h1 className="text-lg font-bold text-blue-600">HIMT ExamPoint</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button-mobile-header"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <div className="p-6">
                <div className="flex items-center mb-6">
                  <img src="/himt-logo.png" alt="HIMT Logo" className="h-10 w-auto mr-2" />
                  <h2 className="text-xl font-bold text-blue-600">HIMT ExamPoint</h2>
                </div>
                {renderNavLinks()}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="mb-4">
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <span className="inline-block mt-2 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                      {user.role}
                    </span>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center mb-8">
            <img src="/himt-logo.png" alt="HIMT Logo" className="h-12 w-auto mr-2" />
            <h2 className="text-xl font-bold text-blue-600">HIMT ExamPoint</h2>
          </div>
          {renderNavLinks()}
        </div>
        
        {/* Fixed Bottom User Section */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
          <div className="mb-3">
            <p className="font-medium text-gray-900 truncate" data-testid="user-name">{user.name}</p>
            <p className="text-sm text-gray-500 truncate" data-testid="user-email">{user.email}</p>
            <span className="inline-block mt-2 px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
              {user.role}
            </span>
          </div>
          <Button
            variant="outline"
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            onClick={handleLogout}
            data-testid="logout-button-desktop"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
}

export default Layout;